/**
 * Route alarms. A proximity alarm radius applied to every waypoint in the route,
 * and a default cross-track-distance (XTD) limit for the whole route. Individual
 * legs can then be tightened or widened from the route's leg list.
 */
import { useEffect, useState } from 'react';
import { useShellStore } from '@/store/shell';
import { useRoutingStore, selectActiveRoute } from '@/store/routing';
import { Sheet } from './Sheet';
import { GlossaryTerm } from '@/help/GlossaryTerm';

export function RouteAlarmsSheet() {
  const route = useRoutingStore(selectActiveRoute);
  const updateRoute = useRoutingStore((s) => s.updateRoute);
  const openSheet = useShellStore((s) => s.openSheet);
  const showToast = useShellStore((s) => s.showToast);

  const [proxOn, setProxOn] = useState(false);
  const [prox, setProx] = useState('0.10');
  const [xtdOn, setXtdOn] = useState(false);
  const [xtd, setXtd] = useState('0.20');

  const rid = route?.id;
  const rprox = route?.arrivalRadiusNm;
  const rxtd = route?.xtdNm;
  useEffect(() => {
    if (!route) return;
    setProxOn(typeof route.arrivalRadiusNm === 'number');
    setProx(route.arrivalRadiusNm != null ? String(route.arrivalRadiusNm) : '0.10');
    setXtdOn(typeof route.xtdNm === 'number');
    setXtd(route.xtdNm != null ? String(route.xtdNm) : '0.20');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rid, rprox, rxtd]);

  if (!route) {
    return <Sheet name="routealarms" title="Route alarms" sub="Build a route first, then set its alarms here." />;
  }

  function save() {
    const r = parseFloat(prox);
    const x = parseFloat(xtd);
    updateRoute(route!.id, {
      arrivalRadiusNm: proxOn && Number.isFinite(r) && r > 0 ? r : undefined,
      xtdNm: xtdOn && Number.isFinite(x) && x > 0 ? x : undefined,
    });
    openSheet('route');
    showToast('Route alarms set');
  }

  return (
    <Sheet
      name="routealarms"
      title="Route alarms"
      sub="A proximity radius on every waypoint and a default cross-track limit. Tap a leg in the route to give it its own XTD."
    >
      <label className="acp-fieldlabel">Proximity alarm on every waypoint</label>
      <div className="acp-seg">
        <button type="button" className={!proxOn ? 'on' : ''} onClick={() => setProxOn(false)}>
          Off
        </button>
        <button type="button" className={proxOn ? 'on' : ''} onClick={() => setProxOn(true)}>
          On
        </button>
      </div>
      {proxOn && (
        <input
          className="acp-text-input"
          value={prox}
          onChange={(e) => setProx(e.target.value)}
          placeholder="0.10"
          inputMode="decimal"
          aria-label="Proximity radius in nautical miles"
        />
      )}
      <label className="acp-fieldlabel">Cross-track alarm (default <GlossaryTerm termKey="xtd">XTD</GlossaryTerm>)</label>
      <div className="acp-seg">
        <button type="button" className={!xtdOn ? 'on' : ''} onClick={() => setXtdOn(false)}>
          Off
        </button>
        <button type="button" className={xtdOn ? 'on' : ''} onClick={() => setXtdOn(true)}>
          On
        </button>
      </div>
      {xtdOn && (
        <input
          className="acp-text-input"
          value={xtd}
          onChange={(e) => setXtd(e.target.value)}
          placeholder="0.20"
          inputMode="decimal"
          aria-label="Default cross-track distance in nautical miles"
        />
      )}
      <div className="acp-btn-row">
        <button type="button" className="acp-btn primary" onClick={save}>
          Save
        </button>
      </div>
    </Sheet>
  );
}
